'use client';
import { useEffect, useState } from 'react';

import Props from '@/components/layout/components/[default]/header/desktop/categories/dropdown/ads-banner/type';
import { Banner } from '@/components/shared/components/cards';
import { BANNER_TYPE } from '@/components/shared/components/cards/banner/type';
import useAdvertisementView from '@/components/shared/helpers/hooks/use-advertisement-view';
import { useIncrementAdvertisementClick } from '@/components/shared/services/hooks';

export default function AdsBanner({ banner, bannerId, show }: Props) {
  const { mutate: incrementAdvertisementClick } = useIncrementAdvertisementClick();
  const { trackView } = useAdvertisementView();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // The dropdown stays mounted while closed (it is only hidden with `invisible`), so a loaded
  // image is not yet a seen image — an impression needs both the load *and* an open menu.
  // `trackView` dedupes by `id`, and both ad slots of a category share the same `id` on the
  // backend (one advertisement record, two images) — so only the first of the two to load fires
  // the request, which is correct: one impression per advertisement, not per image.
  useEffect(() => {
    if (show && isImageLoaded) {
      trackView(banner.id);
    }
  }, [show, isImageLoaded, banner.id, trackView]);

  // `w-full max-w-[261px]`: without an explicit width the card collapses to 0x0 — its only
  // content is an image sized `w-full`, so the width resolves against a zero-width parent, the
  // ad is invisible and its lazy image never loads (which would also make it untrackable).
  return (
    <Banner
      id={bannerId}
      className='max-h-[124px] w-full max-w-[261px] justify-center rounded-[10px]'
      type={BANNER_TYPE.IMAGE}
      link={banner.url}
      onClick={() => incrementAdvertisementClick(banner.id)}
      onLoad={() => setIsImageLoaded(true)}
      width={261}
      height={124}
      title={banner.title}
      image={banner?.image?.publicLink}
    />
  );
}
